<template>
  <div
    class="relative flex w-full items-center justify-start gap-3 rounded px-4 py-3 md:w-max md:min-w-96 md:max-w-[90vw]"
    :class="notification.type?.classes"
  >
    <component
      :is="notification.type?.iconComponent"
      :class="['size-4 shrink-0 ', notification.type?.iconClass]"
    />
    <span class="whitespace-pre-line">{{ notification.message }}</span>
    <template v-for="action in notification.actions">
      <SFLink
        v-if="action.href"
        :key="`link-${action.text}`"
        :class="action.class"
        :to="action.href"
        raw
        class="underline"
        @click="onClick($event, action)"
      >
        {{ action.text }}
      </SFLink>
      <button
        v-else
        :key="action.text"
        :class="action.class"
        class="underline"
        data-testid="toast-info-button"
        @click="onClick($event, action)"
      >
        {{ action.text }}
      </button>
    </template>
    <div
      v-if="notification.withProgress"
      class="absolute bottom-0 left-0 h-1 w-full translate-y-full overflow-hidden rounded-b"
    >
      <div class="relative h-0.5 w-full overflow-hidden">
        <div
          :style="{ width: `${progress}%` }"
          class="h-full max-w-full"
          :class="progressBgClass"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import type {
  NotificationActionHandler,
  StorefrontNotification,
} from '#storefront-ui'
import { useNotification } from '#storefront-ui'
import { SFLink } from '#storefront-ui/components'

const { notification } = defineProps<{ notification: StorefrontNotification }>()
const progress = ref(50)
const { close: closeNotification } = useNotification()

const close = () => closeNotification(notification.id)
const progressBgClass = computed(() => {
  const variant = notification.type?.variant

  switch (variant) {
    case 'SUCCESS':
      return 'bg-green-500'
    case 'INFO':
      return 'bg-info'
    case 'ERROR':
      return 'bg-error'
    default:
      return ''
  }
})
const onClick = (event: Event, action: NotificationActionHandler) => {
  if (!action?.onClick) {
    return
  }
  event.preventDefault()
  event.stopImmediatePropagation()
  action?.onClick({ close })
}

onMounted(async () => {
  if (notification.withProgress) {
    const start = performance.now()
    const animate = (now: number) => {
      const elapsed = now - start
      progress.value = Math.min((elapsed / notification.duration) * 100, 100)

      if (elapsed < notification.duration) {
        requestAnimationFrame(animate)
      }
    }
    requestAnimationFrame(animate)
  }
})

useTimeoutFn(close, notification.duration)
</script>
